import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Inventory, inventoryApi } from '../../../services/gaushala/api';
import { Package, AlertCircle, ArrowLeft, Calendar, User, Warehouse } from 'lucide-react';
import { toast } from 'sonner';

export default function InventoryView() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [inventory, setInventory] = useState<Inventory | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        const fetchData = async () => {
            if (!id) {
                setError("No inventory ID provided");
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                const result = await inventoryApi.getInventoryById(Number(id));
                if (result.success && result.data) {
                    setInventory(result.data);
                } else {
                    setError(result.error || "Failed to fetch inventory");
                    toast.error(result.error || "Failed to fetch inventory");
                }
            } catch (err: any) {
                const errorMsg = err.message || "An error occurred";
                setError(errorMsg);
                toast.error(errorMsg);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [id]);

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading inventory details...</p>
                </div>
            </div>
        );
    }

    if (error || !inventory) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px]">
                <AlertCircle className="h-16 w-16 text-red-500 mb-4" />
                <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Inventory</h2>
                <p className="text-red-600 mb-6">{error || "Inventory not found"}</p>
                <button
                    onClick={() => navigate('/gaushala/inventory')}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                    <ArrowLeft size={20} />
                    Back to Inventory List
                </button>
            </div>
        );
    }

    return (
        <div className="p-6">
            {/* Header */}
            <div className="mb-6">
                <button
                    onClick={() => navigate('/gaushala/inventory')}
                    className="flex items-center gap-2 text-blue-600 hover:text-blue-800 mb-4 transition-colors"
                >
                    <ArrowLeft size={20} />
                    Back to Inventory List
                </button>
                <div className="flex items-center gap-3">
                    <div className="p-3 bg-blue-100 rounded-lg">
                        <Package className="h-8 w-8 text-blue-600" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">{inventory.itemName}</h1>
                        <p className="text-gray-600">Inventory Item Details</p>
                    </div>
                </div>
            </div>

            {/* Details Card */}
            <div className="bg-white rounded-lg shadow-md p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Item ID */}
                    <div className="space-y-1">
                        <label className="text-sm font-medium text-gray-500">Item ID</label>
                        <p className="text-lg font-semibold text-gray-900">#{inventory.id}</p>
                    </div>

                    {/* Item Name */}
                    <div className="space-y-1">
                        <label className="text-sm font-medium text-gray-500">Item Name</label>
                        <p className="text-lg font-semibold text-gray-900">{inventory.itemName}</p>
                    </div>

                    {/* Quantity */}
                    <div className="space-y-1">
                        <label className="text-sm font-medium text-gray-500">Current Quantity</label>
                        <p className="text-lg font-semibold text-blue-600">{inventory.quantity}</p>
                    </div>

                    {/* Unit */}
                    <div className="space-y-1">
                        <label className="text-sm font-medium text-gray-500">Unit</label>
                        <p className="text-lg font-semibold text-gray-900">{inventory.inventoryUnitId || 'N/A'}</p>
                    </div>

                    {/* Type */}
                    <div className="space-y-1">
                        <label className="text-sm font-medium text-gray-500">Type</label>
                        <p className="text-lg font-semibold text-gray-900">{inventory.inventoryTypeId || 'N/A'}</p>
                    </div>

                    {/* Minimum Stock Level */}
                    <div className="space-y-1">
                        <label className="text-sm font-medium text-gray-500">Minimum Stock Level</label>
                        <p className={`text-lg font-semibold ${inventory.quantity < (inventory.minimumStockLevel || 0) ? 'text-red-600' : 'text-green-600'}`}>
                            {inventory.minimumStockLevel || 0}
                            {inventory.quantity < (inventory.minimumStockLevel || 0) && (
                                <span className="ml-2 text-sm text-red-600 font-normal">(Below minimum!)</span>
                            )}
                        </p>
                    </div>

                    {/* Supplier */}
                    <div className="space-y-1">
                        <label className="text-sm font-medium text-gray-500 flex items-center gap-2">
                            <User size={16} />
                            Supplier
                        </label>
                        <p className="text-lg font-semibold text-gray-900">{inventory.supplier || 'N/A'}</p>
                    </div>

                    {/* Gaushala ID */}
                    <div className="space-y-1">
                        <label className="text-sm font-medium text-gray-500 flex items-center gap-2">
                            <Warehouse size={16} />
                            Gaushala ID
                        </label>
                        <p className="text-lg font-semibold text-gray-900">{inventory.gaushalaId}</p>
                    </div>

                    {/* Created At */}
                    <div className="space-y-1 md:col-span-2">
                        <label className="text-sm font-medium text-gray-500 flex items-center gap-2">
                            <Calendar size={16} />
                            Created At
                        </label>
                        <p className="text-lg font-semibold text-gray-900">
                            {inventory.createdAt ? new Date(inventory.createdAt).toLocaleString() : 'N/A'}
                        </p>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="mt-8 flex gap-4">
                    <button
                        onClick={() => navigate(`/gaushala/inventory/edit/${inventory.id}`)}
                        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        Edit Inventory
                    </button>
                    <button
                        onClick={() => navigate(`/gaushala/inventory/${inventory.id}/stock-history`)}
                        className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                    >
                        View Stock History
                    </button>
                </div>
            </div>
        </div>
    );
}

